import _ from 'lodash';
import React, { Fragment, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Button } from 'reactstrap';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import { useAuth0 } from '../../../../auth0/reactAuth0Spa';
import { checkLoginForError, formatBearerToken, informUserEvalLocked } from '../../../../Common.functions';
import { DepartmentContext } from '../../../../DepartmentWrapper';
import { deleteData, postDataAgnostic, putData } from '../../../../Services/dataApi';
import { isPrescreenInterviewDate } from '../../../../Services/interviewDate';
import BinaryQuestion from './BinaryQuestion';
import LikertQuestion from './LikertQuestion';
import TextQuestion from './TextQuestion';
import { feedbackDraftState } from './feedbackDraftState';

let timeoutTime = 10000;

var timeoutHandle;

var MOUSE_MOVE_THRESHOLD = 1000,
  lastMouseMoveTime = -100;

const TimeoutOverlay = styled.div`
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 10;
  button {
    position: fixed;
  }
`;

const CompactSwitch = styled.label`
  display: inline-block;
  height: 17px;
  position: relative;
  width: 30px;

  input {
    height: 0;
    opacity: 0;
    width: 0;
  }

  .slider:before {
    bottom: 2px;
    height: 13px;
    left: 2px;
    width: 13px;
  }

  input:checked + .slider:before {
    -webkit-transform: translateX(13px);
    -ms-transform: translateX(13px);
    transform: translateX(13px);
  }
`;

const Evaluation = ({
  activeTab,
  candidateEvaluation,
  candidateEvaluationStatus,
  candidateInfo,
  candidateSortTypes,
  checkIfStatusNeedsChangingToModify,
  currentCandidateSortType,
  currentlyGettingEval,
  DNRStatus,
  evalStatus,
  evalTab,
  getCandidateEvaluation,
  getCandidateStatus,
  getInfo,
  isAdmin,
  pk_User,
  reduxDate,
  schedule,
  selectedCandidateId,
  selectedCandidate,
  setCandidateEvaluation,
  setCandidateEvaluationStatus,
  setDisableScreen,
  setDNRStatus,
  setEvalStatus,
  Status4SelectedDNR,
  timeoutDurationInMinutes,
  toggleToSchedule,
  toggleToPreviousTab,
  token,
  typeOfEvent,
  fillAvailableHeight = false,
  height = 'calc(100vh - 500px)', // Default height, can be overridden
}) => {
  const [showOverlay, setShowOverlay] = useState(false);
  const [showPreventionOverlay, setShowPreventionOverlay] = useState(false);
  const [AnswersAnswerNumberScore, setAnswersAnswerNumberScore] = useState(null);
  const [questionsNeedingAnswers, setQuestionsNeedingAnswers] = useState({});
  const [count, setCount] = useState(0);
  const [typingQuestions, setTypingQuestions] = useState({});
  const isTyping = Object.values(typingQuestions).some(Boolean);
  const [disableButton, setDisableButton] = useState(false);
  const [questionaireDisabled, setQuestionaireDisabled] = useState(false);
  const [submitQuestions, setSubmitQuestions] = useState(false);
  const [promisesToResolve, setPromisesToResolve] = useState({});
  const [promisesResolving, setPromisesResolving] = useState(false);
  let questionnaireWidth = '100%';
  const activeTabRef = useRef(activeTab);

  const { getTokenSilently, loginWithRedirect } = useAuth0();

  // Alert functionality replaced with toast
  const dContext = useContext(DepartmentContext);

  const { season } = dContext;
  const { EnableEvaluationQuestionnaire, EnablePrescreenQuestionnaire, EnablePrescreenVisibleToAllEvaluators } = season;

  const mode = reduxDate ? (isPrescreenInterviewDate(reduxDate) ? 'Prescreen' : 'Evaluation') : null;
  const answerContextKey = JSON.stringify([
    String(dContext.department.pk_Department ?? ''),
    String(season.pk_Season ?? ''),
    String(pk_User ?? ''),
    String(selectedCandidateId || ''),
    String(reduxDate?.pk_InterviewDate ?? ''),
    mode,
    String(candidateEvaluation?.Status?.pk_EvaluationStatus ?? ''),
  ]);
  const answerContextRef = useRef({ key: answerContextKey, version: 0 });
  const evaluationRef = useRef(candidateEvaluation);
  evaluationRef.current = candidateEvaluation;
  const isMountedRef = useRef(false);
  const updateEvalRef = useRef(null);
  if (answerContextRef.current.key !== answerContextKey) {
    answerContextRef.current = { key: answerContextKey, version: answerContextRef.current.version + 1 };
  }
  const answerContextVersion = answerContextRef.current.version;
  const answerQuestionKeys = JSON.stringify(candidateEvaluation?.evaluation?.map((group) => {
    return group.Questions.map((question) => String(question.pk_EvaluationQuestion));
  }) || []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const recoveredQuestions = {};
    const contextVersion = answerContextRef.current.version;
    const unsubscribers = [];
    evaluationRef.current?.evaluation?.forEach((questionGroup) => {
      questionGroup.Questions.forEach((question) => {
        const questionId = question.pk_EvaluationQuestion;
        const key = `${answerContextKey}:${questionId}`;
        if (feedbackDraftState.getDraft(key) || feedbackDraftState.getWrite(key)) {
          recoveredQuestions[questionId] = true;
        }
        unsubscribers.push(feedbackDraftState.subscribe(key, (event) => {
          if (!isMountedRef.current || answerContextRef.current.version !== contextVersion) {
            return;
          }
          if (event.type === 'available' && !event.result.error) {
            const data = event.result.data;
            setCandidateEvaluation((previous) => {
              if (!previous?.evaluation || answerContextRef.current.version !== contextVersion) {
                return previous;
              }
              return {
                ...previous,
                evaluation: previous.evaluation.map((group) => ({
                  ...group,
                  Questions: group.Questions.map((current) => {
                    if (Number(current.pk_EvaluationQuestion) !== Number(questionId)) {
                      return current;
                    }
                    return feedbackDraftState.markAcknowledgement({
                      ...current,
                      ...(!event.value
                        ? { AnswerText: null, AnswerNumber: null }
                        : Number(data.AnswerTypeNumber1OrText2) === 1
                        ? { AnswerNumber: data.AnswerNumber }
                        : { AnswerText: data.AnswerText }),
                      ...(event.value ? {
                        pk_EvaluationAnswer: data.pk_EvaluationAnswer,
                        AnswerTypeNumber1OrText2: data.AnswerTypeNumber1OrText2,
                      } : {}),
                    });
                  }),
                })),
              };
            });
          }
          const draft = feedbackDraftState.getDraft(key);
          const write = feedbackDraftState.getWrite(key);
          setTypingQuestions((previous) => {
            const next = { ...previous };
            if (draft || write) {
              next[questionId] = true;
            } else {
              delete next[questionId];
            }
            return next;
          });
          // One latest scale intent in bounded page memory; no closure queue. Only
          // a confirmed earlier outcome releases another write in this context.
          if (event.type === 'available' && !event.result.error && draft?.kind === 'scale' && !write) {
            updateEvalRef.current(draft.value, questionId, 'scale');
          }
        }));
      });
    });
    setTypingQuestions(recoveredQuestions);
    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [answerContextKey, answerQuestionKeys, setCandidateEvaluation]);

  useEffect(() => {
    if (candidateEvaluationStatus === 'Complete' || candidateEvaluationStatus === 'Unable To Evaluate') {
      setShowPreventionOverlay(true);
    } else {
      setShowPreventionOverlay(false);
    }
  }, [candidateEvaluationStatus]);

  useEffect(() => {
    if (mode === 'Prescreen' && !EnablePrescreenQuestionnaire) {
      setQuestionaireDisabled(true);
      return;
    }
    if (mode === 'Evaluation' && !EnableEvaluationQuestionnaire) {
      setQuestionaireDisabled(true);
      return;
    }
    setQuestionaireDisabled(false);
  }, [mode, EnableEvaluationQuestionnaire, EnablePrescreenQuestionnaire]);

  // statechange (overriding) orange
  // modification blue
  // creation green
  // destruction red

  // continue evaluation - blue
  // begin - blue
  // modify evlaution - orange
  // zoom - green
  // submit - green
  // unable to evaluate - orange

  // fluid container - darker color

  // no bottom border
  // no schedule or candidates returned - "No Items available for this date"
  // spelled out in date picker

  // change style of document viewer - change icon

  useEffect(() => {
    setDNRStatus(Status4SelectedDNR);
  }, [Status4SelectedDNR]);

  useEffect(() => {
    let readyToSubmit = true;
    for (const property in promisesToResolve) {
      if (promisesToResolve[property] == true) readyToSubmit = false;
    }
    if (readyToSubmit == false && promisesResolving === false) {
      setPromisesResolving(true);
    }

    if (readyToSubmit == true && promisesResolving === true) {
      setPromisesResolving(false);
    }
  }, [promisesToResolve]);

  const submitForm = () => {
    const allRequiredQuestionsAnswered = checkForRequiredAnswers(candidateEvaluation.evaluation[0].Questions);

    const changeEvalStatusToSubmitted = () => {
      changeEvalStatus('Complete', null)
        .then((res) => {
          if (res !== false) {
            const { Status4SelectedStatus } = res;
            const { FirstName, LastName } = selectedCandidate;
            toast.success(`${FirstName} ${LastName}'s evaluation has been submitted`);
            // Swal.fire('Submitted!', `${FirstName} ${LastName}'s evaluation has been submitted`);
            getInfo(true);
            toggleToPreviousTab();
          } else {
            informUserEvalLocked();
          }
        })
        .catch((err) => {
          console.log('err: ', err);

          // toast.error('Evaluation submission failed!');
          Swal.fire('Error', 'Something went wrong...', 'error');
        })
        .finally(() => {
          setDisableScreen(false);
          getInfo(true, true, false, null, null, true);
          setSubmitQuestions(false);
        });
    };

    if (!allRequiredQuestionsAnswered && DNRStatus !== true) {
      Swal.fire(
        'Error',
        `<div>Please answer all required questions (<span class="color_red">*</span>).</div>`,
        'error',
      );
      setDisableScreen(false);
    } else {
      if (DNRStatus === true && !allRequiredQuestionsAnswered) {
        Swal.fire({
          title: 'Warning',
          text: `You have unanswered questions, are you sure you want to submit an incomplete evaluation?`,
          type: 'warning',
          showCancelButton: true,
          confirmButtonColor: 'rgb(48, 133, 214)',
          confirmButtonText: `Submit`,
          cancelButtonColor: 'rgb(221, 51, 51)',
        }).then((result) => {
          if (result.value) {
            changeEvalStatusToSubmitted();

            // setQuestionScale(questionScaleKeys.likert5);
            // saveScaleType(questionScaleKeys.likert5);
            // setHasChange(true);
          }
          setDisableScreen(false);
        });
      } else {
        changeEvalStatusToSubmitted();
      }
    }
  };

  const setEvaluationStatusToInProgress = () => {
    const inProgressStatus = 'In Progress';

    setCandidateEvaluationStatus((prevState) => {
      // if the status is already set to in progress don't do anything
      if (prevState.Status === inProgressStatus) return prevState;

      let prevStateCopy = _.cloneDeep(prevState);
      prevStateCopy.Status = inProgressStatus;
      return prevStateCopy;
    });
  };

  const updateEval = (DisplayValue, pk_EvaluationQuestion, type, callback) => {
    const isCurrentContext = () => {
      return (
        isMountedRef.current &&
        answerContextRef.current.version === answerContextVersion &&
        evaluationRef.current?.evaluation?.some((questionGroup) => {
          return questionGroup.Questions.some((question) => {
            return Number(question.pk_EvaluationQuestion) === Number(pk_EvaluationQuestion);
          });
        })
      );
    };
    const queueKey = `${answerContextKey}:${pk_EvaluationQuestion}`;
    if (type === 'scale') {
      const previousDraft = feedbackDraftState.getDraft(queueKey);
      if (!feedbackDraftState.retain(queueKey, { kind: 'scale', value: DisplayValue, version: (previousDraft?.version || 0) + 1 })) {
        return Promise.resolve({ error: true, capacity: true });
      }
      setTypingQuestions((previous) => ({ ...previous, [pk_EvaluationQuestion]: true }));
    }
    const request = feedbackDraftState.runWrite(queueKey, DisplayValue, async () => {
      if (!isCurrentContext()) {
        return { error: true, stale: true, cancelled: true };
      }
      let hasIssuedWrite = false;
      try {
        const answerToken = await getTokenSilently().catch((err) => {
          if (isCurrentContext()) {
            checkLoginForError({ err, loginWithRedirect });
          }
          throw err;
        });
        if (!isCurrentContext()) {
          return { error: true, stale: true, cancelled: true };
        }
        const params = {
          pk_EvaluationQuestion,
          pk_Department: dContext.department.pk_Department,
          pk_Season: season.pk_Season,
          pk_EvaluationStatus: candidateEvaluation.Status.pk_EvaluationStatus,
          type,
        };
        hasIssuedWrite = true;
        const result = DisplayValue
          ? await postDataAgnostic(
              'department/season/candidate/answer',
              params,
              { DisplayValue },
              formatBearerToken(answerToken),
            )
          : await deleteData(
              'department/season/candidate/answer',
              { ...params, DisplayValue },
              null,
              formatBearerToken(answerToken),
            );
        // The server outcome still matters after unmount: it controls the write
        // fence and the baseline of any recoverable newer draft, never another UI.
        const data = result?.data;
        const responseQuestionId = DisplayValue ? data?.fk_EvaluationQuestion : data?.pk_EvaluationQuestion;
        if (result?.error || data?.error || !data || Number(responseQuestionId) !== Number(pk_EvaluationQuestion)) {
          throw new Error('Unable to verify saved answer');
        }
        if (type === 'scale' && DisplayValue && (Number(data.AnswerTypeNumber1OrText2) !== 1 || Number(data.AnswerNumber) !== Number(DisplayValue))) {
          throw new Error('Unable to verify saved score');
        }
        if (type === 'text' && DisplayValue && data.AnswerText !== DisplayValue) {
          throw new Error('Unable to verify saved answer text');
        }
        if (isCurrentContext()) {
          setEvaluationStatusToInProgress();
        }
        return { error: false, data };
      } catch (err) {
        if (isCurrentContext()) {
          toast.error('Unable to save');
        }
        const status = err.response?.status;
        const isRefused = status === 401 || status === 403;
        return { error: true, uncertain: hasIssuedWrite && !isRefused };
      }
    });
    return request.then((result) => {
      if (callback && isCurrentContext() && !result.waiting && !feedbackDraftState.getDraft(queueKey)) {
        callback(result);
      }
      return result;
    });
  };

  updateEvalRef.current = updateEval;

  const changeEvalStatus = (status, enableDoNotRank) => {
    console.log('selectedCandidateId: ', selectedCandidateId);

    if (!selectedCandidateId) return Promise.reject(new Error('No selected candidate ID'));
    setDisableScreen(true);
    return getTokenSilently(token)
      .then((token) => {
        setDisableButton(false);
        return putData(
          'department/season/candidate/evaluation/status',
          {
            pk_Department: dContext.department.pk_Department,
            pk_Season: dContext.season.pk_Season,
            pk_User,
            enableDoNotRank,
            selectedCandidateId: selectedCandidateId,
            isAdmin,
            status,
            mode,
          },
          null,
          formatBearerToken(token),
        );
      })
      .then((result) => {
        if (result.data && result.data.locked && result.data.locked === true) {
          return false;
        } else {
          getTokenSilently(token)
            .then((token) => {
              getCandidateStatus(selectedCandidateId, token);
            })
            .catch((err) => {
              checkLoginForError({ err, loginWithRedirect });
            });
          return result;
        }
      })
      .catch((err) => {
        checkLoginForError({ err, loginWithRedirect });
        return false;
      })
      .finally(() => {
        setDisableScreen(false);
      });
  };

  const handleMouseMove = useCallback((event) => {
    if (activeTabRef.current === evalTab) {
      var now = +new Date();
      if (now - lastMouseMoveTime < MOUSE_MOVE_THRESHOLD) return;
      lastMouseMoveTime = now;

      window.clearTimeout(timeoutHandle);
      timeoutHandle = window.setTimeout(function() {
        setShowOverlay(true);
      }, timeoutDurationInMinutes * 60000);
    }
  }, []);

  const checkForRequiredAnswers = (Questions) => {
    setQuestionsNeedingAnswers({});
    let allRequiredAnswered = true;
    const questionsNeedToAnswer = {};
    Questions.forEach((question, i) => {
      const { AnswerNumber, AnswerText, AnswerTypeNumber1OrText2, EnableRequired, pk_EvaluationQuestion } = question;
      // check to see if question has any answer in them for all types
      let isRequiredAndHasValue = true;
      if (EnableRequired) {
        if (AnswerTypeNumber1OrText2 === 1) {
          if (AnswerNumber === null || AnswerNumber === undefined) {
            isRequiredAndHasValue = false;
          }
        } else {
          if (AnswerText === null || AnswerText === undefined || (AnswerText && AnswerText.trim() === '')) {
            isRequiredAndHasValue = false;
          }
        }
      }
      if (!isRequiredAndHasValue) {
        questionsNeedToAnswer[pk_EvaluationQuestion] = true;
        allRequiredAnswered = false;
      }
    });
    setQuestionsNeedingAnswers(questionsNeedToAnswer);
    return allRequiredAnswered;
  };

  // check for any answers. This will be necessary if the user wants to wipe the evaluation for any reason
  const checkForAnyAnswer = (questions) => {
    let hasAnswers = false;
    questions.forEach((question) => {
      if (question.AnswerTypeNumber1OrText2 === 1 && question.AnswerNumber !== null) {
        hasAnswers = true;
      } else if (question.AnswerText != null || (question.AnswerText && question.AnswerText.trim() !== '')) {
        hasAnswers = true;
      }
    });
    return hasAnswers;
  };

  const handleTouchStart = useCallback((event) => {
    if (activeTabRef.current === evalTab) {
      window.clearTimeout(timeoutHandle);
      timeoutHandle = window.setTimeout(function() {
        setShowOverlay(true);
      }, timeoutDurationInMinutes * 60000);
    }
  }, []);

  useEffect(() => {
    activeTabRef.current = activeTab;
    // if user is on eval tab start timeout
    if (activeTab === evalTab) {
      window.clearTimeout(timeoutHandle);
      timeoutHandle = window.setTimeout(function() {
        setShowOverlay(true);
      }, timeoutTime);
      return;
    }
    window.clearTimeout(timeoutHandle);
  }, [activeTab]);

  const updateAnswersInDatabase = (index) => {
    //candidateEvaluation
  };

  useEffect(() => {
    if (typeOfEvent === 'mousemove') {
      window.addEventListener(typeOfEvent, handleMouseMove);

      return () => {
        window.removeEventListener(typeOfEvent, handleMouseMove);
      };
    }
    if (typeOfEvent === 'touchstart') {
      window.addEventListener(typeOfEvent, handleTouchStart);

      return () => {
        window.removeEventListener(typeOfEvent, handleTouchStart);
      };
    }
  }, [handleMouseMove, handleTouchStart]);

  const handleEvalChange = () => {
    changeEvalStatus('UnableToEvaluate', null)
      .then((res) => {
        if (res !== false) {
          const { FirstName, LastName } = selectedCandidate;
          toast.success(`${FirstName} ${LastName}'s evaluation has been submitted`);
          // Swal.fire('Submitted!', `${FirstName} ${LastName}'s evaluation has been submitted`);
          getInfo(true, true, true);
          toggleToPreviousTab();
        } else {
          informUserEvalLocked();
        }
        setDisableScreen(false);
      })
      .catch((err) => {
        setDisableScreen(false);
        Swal.fire('Error', 'Something went wrong...', 'error');
      });
  };

  const alertUserDisabled = () => {
    Swal.fire(
      'Questionnaire is Disabled',
      'Your Coordinator can enable: Settings → Evaluation → Evaluation Questionnaire → Enable for Evaluators.',
      'warning',
    );
  };

  const alertUserForChange = () => {
    Swal.fire({
      title: 'Do you want to modify this evaluation?',
      text: `Status will be set to "In progress."`,
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: 'rgb(48, 133, 214)',
      confirmButtonText: `Modify`,
      cancelButtonColor: 'rgb(221, 51, 51)',
    }).then((result) => {
      if (result.value) {
        setShowPreventionOverlay(false);
        changeEvalStatus('InProgress', false).then((result) => {
          if (result === false) {
            informUserEvalLocked();
          }
        });
        // setQuestionScale(questionScaleKeys.likert5);
        // saveScaleType(questionScaleKeys.likert5);
        // setHasChange(true);
      }
    });
  };

  const addPrescreenComments = (question) => {
    if (question.PrescreenCombinedComments !== null && mode === 'Evaluation' && EnablePrescreenVisibleToAllEvaluators) {
      const prescreenComments = question.PrescreenComments || [];
      let loggedInEvaluatorComment = '';

      prescreenComments.sort((a, b) => {
        const nameA = a.EvaluatorName.toUpperCase();
        const nameB = b.EvaluatorName.toUpperCase();

        if (nameA < nameB) {
          return -1;
        }

        if (nameA > nameB) {
          return 1;
        }

        return 0;
      });

      let concatenatedPrescreenComment = loggedInEvaluatorComment;

      for (const comment of prescreenComments) {
        if (comment['AnswerText'].search('bold') === -1) concatenatedPrescreenComment += comment.AnswerText;
        else concatenatedPrescreenComment = comment.AnswerText + concatenatedPrescreenComment;
      }

      return (
        <>
          <h6 style={{ fontSize: '14px', fontWeight: 'bold' }}>Prescreen Comments:</h6>
          <div dangerouslySetInnerHTML={{ __html: concatenatedPrescreenComment || '---' }}></div>
        </>
      );
    } else if (question.UserPrescreenComment && mode === 'Evaluation') {
      return (
        <>
          <span style={{ fontWeight: 'bold', marginTop: 5 }}>Your Prescreen Comment: </span>
          <span>{question.UserPrescreenComment || '---'} </span>
        </>
      );
    }
  };

  const addPrescreenAvg = (question) => {
    if (question.PrescreenAverageScore && mode === 'Evaluation' && EnablePrescreenVisibleToAllEvaluators) {
      return (
        <>
          <span style={{ fontWeight: 'bold', marginTop: 5 }}>Prescreen Average Score:</span>{' '}
          <span>{question.PrescreenAverageScore} </span>
          {question.UserPrescreenScore ? (
            <>
              <br />
              <span style={{ marginTop: 4 }}>Your Prescreen Score: </span>
              <span>{question.UserPrescreenScore} </span>
            </>
          ) : null}
        </>
      );
    } else if (question.UserPrescreenScore && mode === 'Evaluation') {
      return (
        <>
          <span style={{ fontWeight: 'bold', marginTop: 5 }}>Your Prescreen Score: </span>
          <span>{question.UserPrescreenScore} </span>
        </>
      );
    }
  };

  const determineQuestion = (question, i, questionOptions) => {
    const { CustomQuestionOptions } = question;
    const { pk_EvaluationQuestion } = question;
    const { QuestionType } = question;

    // if (CustomQuestionOptions) {
    //   questionnaireWidth = 'calc(100vw - 60%)';
    // }

    const flagAsUnanswered = questionsNeedingAnswers[pk_EvaluationQuestion];
    const alternatingColor = i % 2 ? 'var(--rr-surface-sunken)' : 'var(--rr-surface)';
    const backgroundColor = flagAsUnanswered ? 'var(--rr-danger-soft)' : alternatingColor;

    let type = '';

    const questionTypeCapitalized = QuestionType.toUpperCase();

    //at one point type was defined numerically but got changed to string.
    if (QuestionType === '0' || questionTypeCapitalized === 'TEXT') {
      type = 'text';
    }

    if (QuestionType === '1' || questionTypeCapitalized === 'SCALE') {
      type = 'scale';
    }

    if (QuestionType === '2' || questionTypeCapitalized === 'YES/NO') {
      type = 'yes/no';
    }

    if (type === 'scale') {
      return (
        <div
          key={`${answerContextKey}:${question.pk_EvaluationQuestion}`}
          style={{ backgroundColor: backgroundColor, padding: '20px', width: questionnaireWidth }}
        >
          <LikertQuestion
            draftKey={`${answerContextKey}:${question.pk_EvaluationQuestion}`}
            question={question}
            questionOptions={CustomQuestionOptions || questionOptions}
            i={i}
            updateEval={updateEval}
            updateAnswersInDatabase={updateAnswersInDatabase}
            Answers4QuestionsAnswerNumberScore={question.Answers4QuestionsAnswerNumberScore}
            checkIfStatusNeedsChangingToModify={checkIfStatusNeedsChangingToModify}
            questionaireDisabled={questionaireDisabled}
          />
          {feedbackDraftState.getDraft(`${answerContextKey}:${question.pk_EvaluationQuestion}`)?.kind === 'scale' && (
            <div role="status">
              {feedbackDraftState.getWrite(`${answerContextKey}:${question.pk_EvaluationQuestion}`)?.uncertain
                ? 'Save outcome unknown. Keep this page open and copy your score; contact support before retrying.'
                : feedbackDraftState.getWrite(`${answerContextKey}:${question.pk_EvaluationQuestion}`)
                ? 'Saving score...'
                : <button onClick={() => updateEval(feedbackDraftState.getDraft(`${answerContextKey}:${question.pk_EvaluationQuestion}`).value, question.pk_EvaluationQuestion, 'scale')}>Retry score save</button>}
            </div>
          )}
          {addPrescreenAvg(question)}
        </div>
      );
    }

    if (type === 'yes/no') {
      return (
        <div
          key={`${answerContextKey}:${question.pk_EvaluationQuestion}`}
          style={{ backgroundColor: backgroundColor, padding: '20px', width: questionnaireWidth }}
        >
          <BinaryQuestion
            question={question}
            i={i}
            updateAnswersInDatabase={updateAnswersInDatabase}
            updateEval={updateEval}
            checkIfStatusNeedsChangingToModify={checkIfStatusNeedsChangingToModify}
            questionaireDisabled={questionaireDisabled}
          />
        </div>
      );
    }

    if (type === 'text') {
      return (
        <div
          key={`${answerContextKey}:${question.pk_EvaluationQuestion}`}
          style={{ backgroundColor: backgroundColor, padding: '20px', width: questionnaireWidth }}
        >
          <TextQuestion
            draftKey={`${answerContextKey}:${question.pk_EvaluationQuestion}`}
            questionaireDisabled={questionaireDisabled}
            question={question}
            updateAnswersInDatabase={updateAnswersInDatabase}
            setIsTyping={(isQuestionTyping) => {
              if (!isMountedRef.current || answerContextRef.current.version !== answerContextVersion) {
                return;
              }
              setTypingQuestions((previousQuestions) => {
                const nextQuestions = { ...previousQuestions };
                if (isQuestionTyping || feedbackDraftState.getDraft(`${answerContextKey}:${question.pk_EvaluationQuestion}`) || feedbackDraftState.getWrite(`${answerContextKey}:${question.pk_EvaluationQuestion}`)) {
                  nextQuestions[question.pk_EvaluationQuestion] = true;
                } else {
                  delete nextQuestions[question.pk_EvaluationQuestion];
                }
                return nextQuestions;
              });
            }}
            i={i}
            updateEval={updateEval}
            answerText={question.AnswerText}
            checkIfStatusNeedsChangingToModify={checkIfStatusNeedsChangingToModify}
          />
          {addPrescreenComments(question)}
        </div>
      );
    }

    return <div></div>;
  };

  if (!candidateEvaluation) return <div />;

  if (candidateEvaluation === 'error') return <div />;

  return (
    <div
      style={
        fillAvailableHeight
          ? { display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }
          : undefined
      }
    >
      <div
        style={{
          padding: '10px',
          width: '100%',
          height: fillAvailableHeight ? undefined : height,
          flex: fillAvailableHeight ? 1 : undefined,
          minHeight: fillAvailableHeight ? 0 : undefined,
          overflow: 'auto',
          position: 'relative',
        }}
      >
        {(showPreventionOverlay || questionaireDisabled) && (
          <div
            onClick={(e) => {
              e.stopPropagation();
              if (questionaireDisabled) {
                alertUserDisabled();
              } else {
                alertUserForChange();
              }
            }}
            style={{
              height: '100%',
              width: 'calc(100% - 20px)',
              position: 'absolute',
              cursor: 'pointer',
              top: '0',
              zIndex: '1',
            }}
          ></div>
        )}
        {candidateEvaluation && candidateEvaluation.evaluation && candidateEvaluation.evaluation.length > 0 ? (
          candidateEvaluation.evaluation.map((questionObject) => {
            return questionObject.Questions.map((question, i) => {
              return determineQuestion(question, i, candidateEvaluation.questionOptions);
            });
          })
        ) : (
          <div
            style={{
              height: '40vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
            }}
          >
            No Evaluation is available
          </div>
        )}
      </div>
      <div style={{ display: 'flex', flexShrink: fillAvailableHeight ? 0 : undefined, justifyContent: 'space-around', padding: 15 }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginTop: 'auto',
            marginBottom: 'auto',
          }}
        >
          <CompactSwitch>
            <input
              tabIndex={questionaireDisabled ? -1 : 0}
              type="checkbox"
              id={`DNR_${selectedCandidateId}`}
              disabled={disableButton}
              checked={DNRStatus || false}
              onClick={async () => {
                const continueWorkflow = await checkIfStatusNeedsChangingToModify();
                console.log('continueWorkflow: ', continueWorkflow);
                console.log('DNRStatus: ', DNRStatus);
                if (!continueWorkflow) {
                  return;
                }
                setDisableButton(true);

                if (DNRStatus) {
                  setDNRStatus(false);
                  changeEvalStatus(null, false).then((result) => {
                    if (result === false) {
                      informUserEvalLocked();
                      setDNRStatus(true);
                    }
                  });
                } else {
                  setDNRStatus(true);
                  changeEvalStatus(null, true).then((result) => {
                    if (result === false) {
                      informUserEvalLocked();
                      setDNRStatus(false);
                    }
                  });
                }
              }}
            />
            <span className="slider round"></span>
          </CompactSwitch>
          Do Not Rank
        </div>
        <div
          style={{
            marginTop: 'auto',
            marginBottom: 'auto',
          }}
        >
          <Button
            tabIndex={questionaireDisabled ? -1 : 0}
            disabled={questionaireDisabled || promisesResolving || isTyping}
            onClick={async (e) => {
              const continueWorkflow = await checkIfStatusNeedsChangingToModify('Unable To Evaluate');
              if (continueWorkflow === false) {
                return;
              }

              const hasAnswers = checkForAnyAnswer(candidateEvaluation.evaluation[0].Questions);
              e.preventDefault();
              if (hasAnswers) {
                const { FirstName, LastName } = selectedCandidate;
                return Swal.fire({
                  title: 'Warning',
                  text: `This will delete any feedback you have already entered for ${FirstName ||
                    'Candidate'} ${LastName || ''}.`,
                  icon: 'warning',
                  showCancelButton: true,
                  confirmButtonColor: '#3085d6',
                  cancelButtonColor: '#d33',
                  confirmButtonText: 'Discard Evaluation',
                }).then((result) => {
                  if (result.value === true) {
                    setDisableScreen(true);
                    handleEvalChange();
                  }
                });
              } else {
                setDisableScreen(true);
                handleEvalChange();
              }
            }}
          >
            Unable To Evaluate
          </Button>
        </div>
        <div
          style={{
            marginTop: 'auto',
            marginBottom: 'auto',
          }}
        >
          <p style={{ margin: 0, padding: 0 }}>
            Questions marked with "<span style={{ color: '#ff0000' }}>*</span>" are required
          </p>
        </div>
        <div
          style={{
            marginTop: 'auto',
            marginBottom: 'auto',
          }}
        >
          <Button
            tabIndex={questionaireDisabled ? -1 : 0}
            disabled={promisesResolving || isTyping || questionaireDisabled}
            onClick={async (e) => {
              e.preventDefault();
              const continueWorkflow = await checkIfStatusNeedsChangingToModify();

              if (continueWorkflow) {
                setDisableScreen(true);
                submitForm();
              }
            }}
          >
            {promisesResolving || isTyping ? 'Saving...' : 'Submit'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Evaluation;
